/**
 * Pure reconciliation between the modal history we track and the modal the URL
 * currently names. This is the whole of the Back/Forward contract, kept free of
 * React and the router so it can be unit-tested directly.
 *
 * Terminology: `entries` is outermost-modal-first (the order they were opened);
 * the URL only ever names ONE modal — the innermost one.
 */

import type { ConfiguredModalId } from "../url/modal-url-config";
import type { ModalHistoryEntry } from "./modal-history-store";

export interface ModalHistoryReconciliation {
  /** Modals to close, innermost first (so nested stacks unwind in order). */
  toClose: ConfiguredModalId[];
  /** Modal to open, if the URL names one we are not already tracking. */
  toOpen: ConfiguredModalId | null;
  /** Entries to keep tracking after this reconciliation. */
  nextEntries: ModalHistoryEntry[];
}

function closeAll(entries: readonly ModalHistoryEntry[]): ConfiguredModalId[] {
  return entries.map((entry) => entry.modalId).reverse();
}

/**
 * Decide what to open/close so the modal stack matches the URL.
 *
 * - URL names no modal → close everything we track (user pressed Back past the
 *   first modal, or navigated to a clean URL).
 * - URL names a modal we already track → close everything opened *after* it
 *   (Back out of a nested modal) and keep the rest.
 * - URL names a modal we do not track → the tracked entries belong to a
 *   different history position (Forward after close, or a deep link). Close
 *   them and open the one the URL asks for.
 */
export function reconcileModalHistory(
  entries: readonly ModalHistoryEntry[],
  urlModalId: ConfiguredModalId | null,
  urlEntityId: string | null = null
): ModalHistoryReconciliation {
  if (!urlModalId) {
    return { toClose: closeAll(entries), toOpen: null, nextEntries: [] };
  }

  const index = entries.findIndex((entry) => entry.modalId === urlModalId);

  if (index === -1) {
    return {
      toClose: closeAll(entries),
      toOpen: urlModalId,
      // Arrived here by Forward/deep-link rather than by our own `push`, so the
      // modal owns the CURRENT history entry — closing it rewrites in place
      // and there is no earlier URL of ours to unwind to.
      nextEntries: [
        {
          modalId: urlModalId,
          entityId: urlEntityId,
          pushed: false,
          urlBefore: null,
          observedOpen: false,
        },
      ],
    };
  }

  // Back into a modal we already track. Keep its `pushed`/`urlBefore`
  // bookkeeping — it still owns the entry it originally created — but adopt
  // the entity the URL names, which is how a Back across an entity switch
  // (prev/next inside the modal) lands on the right record.
  const kept = entries.slice(0, index + 1).map((entry, position) =>
    position === index ? { ...entry, entityId: urlEntityId } : entry
  );

  return {
    toClose: closeAll(entries.slice(index + 1)),
    toOpen: null,
    nextEntries: kept,
  };
}
